if (!window.ShippingProgressBar) {
  class ShippingProgressBar extends HTMLElement {
    constructor() {
      super();
      this.threshold = parseInt(this.dataset.threshold) || 5000;
      this.updateInterval = 5000;
      this.intervalId = null;
      this.sectionId = this.dataset.sectionId || 'global';
      this.weightUnit = this.dataset.weightUnit || 'kg';
      this.discountCode = window.shippingProgressDiscountCode || 'FREE_SHIPPING';
      this.isDiscountApplied = null;
    }

    connectedCallback() {
      this.#initializeProgress();
      this.#startPolling();
    }

    disconnectedCallback() {
      this.#stopPolling();
    }

    #initializeProgress() {
      this.progressFill = this.querySelector('.shipping-progress__fill');
      this.progressText = this.querySelector('.shipping-progress__text');

      if (!this.progressFill || !this.progressText) {
        console.warn('Shipping progress bar: Required elements not found');
        return;
      }

      this.#updateDisplay(parseInt(this.dataset.current) || 0);
    }

    #startPolling() {
      // Update immediately, then set interval
      this.#fetchCartData();

      this.intervalId = setInterval(() => {
        this.#fetchCartData();
      }, this.updateInterval);
    }

    #stopPolling() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }

    async #fetchCartData() {
      try {
        const response = await fetch('/cart.json');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const cartData = await response.json();
        const totalWeight = this.#calculateTotalWeight(cartData.items);

        this.#updateDisplay(totalWeight);
      } catch (error) {
        console.warn('Failed to fetch cart data:', error);
      }
    }

    #calculateTotalWeight(items) {
      if (!items || items.length === 0) return 0;

      return items.reduce((total, item) => {
        const itemWeight = item.grams || 0;
        const quantity = item.quantity || 0;
        return total + (itemWeight * quantity);
      }, 0);
    }

    #formatWeight(grams) {
      if (this.weightUnit === 'kg') {
        const kg = grams / 1000;
        return kg.toFixed(2).replace(/\.?0+$/, '');
      }
      return Math.round(grams);
    }

    #getWeightUnit() {
      return this.weightUnit === 'kg' ? 'kg' : 'g';
    }

    async #updateDisplay(currentWeight) {
      if (!this.progressFill || !this.progressText) return;

      const remaining = Math.max(0, this.threshold - currentWeight);
      const progressPercentage = Math.min(100, (currentWeight / this.threshold) * 100);

      // Update progress bar
      this.progressFill.style.width = `${progressPercentage}%`;

      const formattedRemaining = this.#formatWeight(remaining);
      const weightUnit = this.#getWeightUnit();

      // Update text
      if (currentWeight === 0) {
        const texts = window.shippingProgressTexts?.[this.sectionId];
        const emptyText = texts?.empty || 'Add items to your cart to see shipping progress';
        this.progressText.textContent = emptyText;
        if (this.isDiscountApplied || this.isDiscountApplied === null) {
          await this.toggleDiscount(false);
        }
      } else if (remaining > 0) {
        const isVertical = this.classList.contains('shipping-progress--vertical');
        const isMobile = window.innerWidth <= 749;

        const texts = window.shippingProgressTexts?.[this.sectionId];
        let textTemplate;

        if (isVertical || isMobile) {
          textTemplate = texts?.mobile || 'Add [weight] more to get free shipping';
        } else {
          textTemplate = texts?.desktop || 'Add [weight] more to get free shipping';
        }

        this.progressText.textContent = textTemplate
          .replace('[weight]', `${formattedRemaining}${weightUnit}`);
        if (this.isDiscountApplied || this.isDiscountApplied === null) {
          await this.toggleDiscount(false);
        }
      } else {
        const texts = window.shippingProgressTexts?.[this.sectionId];
        const qualifiedText = texts?.qualified || '🎉 You qualify for free shipping!';
        this.progressText.textContent = qualifiedText;
        if (!this.isDiscountApplied || this.isDiscountApplied === null) {
          await this.toggleDiscount(true);
        }
      }

      // Update data attributes
      this.dataset.current = currentWeight;
    }

    async toggleDiscount(apply) {
      
      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            discount: apply ? this.discountCode : ''
          })
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        this.isDiscountApplied = apply;
        return data;
      } catch (error) {
        this.isDiscountApplied = false;
        throw error;
      }
    }

    // Public method to manually update (useful for cart events)
    updateProgress(newWeight) {
      this.#updateDisplay(newWeight);
    }

    // Public method to update with cart items directly
    updateProgressFromItems(items) {
      const totalWeight = this.#calculateTotalWeight(items);
      this.#updateDisplay(totalWeight);
    }

    // Public method to pause/resume polling
    pausePolling() {
      this.#stopPolling();
    }

    resumePolling() {
      this.#startPolling();
    }
  }

  // Define the custom element
  customElements.define('shipping-progress-bar', ShippingProgressBar);

  // Initialize translations if not already available
  if (!window.shopifyTranslations) {
    window.shopifyTranslations = {};
  }

  // Export for use in other scripts if needed
  window.ShippingProgressBar = ShippingProgressBar;

}
